import { useState, useRef, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Switch,
  Text,
  Textarea,
  FileButton,
  Group,
  Loader,
  Image,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconPhotoOff, IconPhotoUp, IconTrash } from '@tabler/icons-react'
import clsx from 'clsx'
import { useCreateArenaPost } from './api'
import { useGetAsset, useUploadAsset } from '../assets/api'
import classes from './Arena.module.css'
import { useParams } from 'react-router'
import AvatarMe from '../auth/AvatarMe'

export default function CreatePost({
  isEditing,
  isScrolled,
}: {
  isEditing?: number
  isScrolled: boolean
}) {
  const [status, setStatus] = useState<string>('')
  const [isRoar, setIsRoar] = useState<boolean>(false)
  const [file, setFile] = useState<File | null>(null)
  const [asset_id, setAssetId] = useState<number | null>(null)
  const resetRef = useRef<() => void>(null)
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const queryClient = useQueryClient()
  const { mutate: createArenaPost, isPending } = useCreateArenaPost()
  const { mutate: addAsset, isPending: addAssetPending } = useUploadAsset()
  const { data: asset, isFetching: assetIsFetching } = useGetAsset({
    asset_id: asset_id,
  })
  const { competitionId } = useParams()

  function clearFile() {
    setFile(null)
    setAssetId(null)
    resetRef.current?.()
  }

  function handleAddArenaPost() {
    const isVideo = file?.type.startsWith('video/')
    createArenaPost(
      {
        competition_id: Number(competitionId),
        is_roar: isRoar,
        text: isRoar ? status.slice(0, 50) : status,
        image_id: !isVideo ? asset_id : null,
        video_id: isVideo ? asset_id : null,
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ['arenaPosts'],
          })
          await queryClient.invalidateQueries({
            queryKey: ['unseenContent'],
          })
          setIsRoar(false)
          setStatus('')
          clearFile()
        },
      }
    )
  }

  useEffect(() => {
    if (file) {
      const mediaType = file.type.startsWith('video/') ? 'video' : 'image'
      addAsset(
        {
          file: file,
          model_field: `social.Post.${mediaType}`,
        },
        {
          onSuccess: (asset_id: number) => {
            setAssetId(asset_id)
          },
        }
      )
    }
  }, [file, addAsset])

  useEffect(() => {
    if (isRoar && file) {
      clearFile()
    }
  }, [isRoar])

  return (
    <div
      className={clsx(
        classes.createPostWrapper,
        {
          [classes.desktopPostWrapper]: isDesktop,
        },
        { [classes.blurred]: !!isEditing }
      )}
    >
      <div
        className={clsx(classes.createPostContainer, {
          [classes.hidePostContainer]: isScrolled && !isDesktop,
        })}
      >
        <div className={classes.createPostStatus}>
          <AvatarMe size="md" />
          <Textarea
            classNames={{
              root: classes.textareaRoot,
              input: classes.textareaInput,
            }}
            autosize
            placeholder="What's happening?"
            maxLength={isRoar ? 50 : 280}
            value={status}
            onChange={(event) => setStatus(event.currentTarget.value)}
          />
        </div>
        {!isRoar && file && asset && 'url' in asset.main && (
          <div className={classes.assetContainer}>
            {file?.type.startsWith('video/') ? (
              <video
                src={asset.main.url}
                controls
                className={classes.postAsset}
              />
            ) : (
              <Image src={asset.main.url} className={classes.postAsset} />
            )}
            <div style={{ position: 'absolute', top: 8, right: 8 }}>
              <IconTrash
                style={{
                  cursor: 'pointer',
                  background: 'rgba(0,0,0,0.5)',
                  borderRadius: '4px',
                  padding: '4px',
                }}
                width={24}
                height={24}
                color="white"
                onClick={clearFile}
              />
            </div>
          </div>
        )}
        {!isRoar &&
          (addAssetPending ||
            assetIsFetching ||
            (asset && 'task_id' in asset.main)) && (
            <div style={{ marginTop: '12px' }}>
              <Group>
                <Loader size="sm" color="#0B6B7B" />
                <Text size="sm">Uploading media...</Text>
              </Group>
            </div>
          )}
        <div className={classes.createPostAdditionalInfo}>
          <div>
            <Switch
              classNames={{
                label: classes.switchLabel,
                body: classes.switchBody,
                track: classes.switchTrack,
                thumb: classes.switchThumb,
              }}
              labelPosition="left"
              label="Roar"
              checked={isRoar}
              withThumbIndicator={false}
              onChange={(event) => setIsRoar(event.currentTarget.checked)}
            />
            {isRoar ? (
              <IconPhotoOff width={20} height={20} color="#868E96" />
            ) : (
              <FileButton
                resetRef={resetRef}
                onChange={setFile}
                accept="image/png,image/jpeg,image/jpg,video/mp4,video/mov,video/avi,video/webm"
                disabled={isRoar}
              >
                {(props) => (
                  <IconPhotoUp
                    {...props}
                    className={clsx(classes.assetIcon, {
                      [classes.roarAssetIcon]: isRoar,
                    })}
                  />
                )}
              </FileButton>
            )}
          </div>
          <div>
            <Text component="p">
              {status.length} / {isRoar ? 50 : 280}
            </Text>
            <Button
              loading={isPending}
              disabled={
                isPending ||
                status.length === 0 ||
                (isRoar && status.length > 50)
              }
              onClick={handleAddArenaPost}
            >
              Post
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
